import { Routes } from '@angular/router';
import { UserRole } from '@const/users.enum';
import { activeUserGuard } from './guards/active-user.guard';
import { entryPointGuard, navigationAccessGuard } from './guards/navigation-access.guard';
import { roleGuard } from './guards/role.guard';

export const routes: Routes = [
  { path: '', redirectTo: '/profile', pathMatch: 'full' },

  // Login page - unauthenticated landing page (no guards)
  {
    path: 'login',
    loadComponent: () => import('./features/login/login.component').then((c) => c.LoginComponent),
  },

  // Field demo (for testing)
  // {
  //   path: 'field-demo',
  //   loadComponent: () =>
  //     import('./components/field-demo/field-demo.component').then((c) => c.FieldDemoComponent),
  //   data: { breadcrumb: 'Field Demo' },
  // },

  // Profile routes - accessible by all authenticated users (entry point)
  // Note: activeUserGuard is applied but allows /profile/disable for deactivated users
  {
    path: 'profile',
    canActivate: [activeUserGuard, entryPointGuard],
    loadChildren: () => import('./features/profile/profile.module').then((m) => m.ProfileModule),
    data: { breadcrumb: 'breadcrumb.profile' },
  },

  // Maintenance routes - Admin and Tech Admin only
  // CRITICAL: activeUserGuard checks if user is deactivated BEFORE role checks
  {
    path: 'maintenance',
    canActivate: [activeUserGuard, navigationAccessGuard, roleGuard],
    loadChildren: () =>
      import('./features/maintenance/maintenance.module').then((m) => m.MaintenanceModule),
    data: {
      breadcrumb: 'breadcrumb.maintenance',
      requiredRoles: [UserRole.ADMIN, UserRole.TECH_ADMIN],
    },
  },

  // System Settings routes - Admin, Operational Admin, and Tech Admin
  {
    path: 'system-settings',
    canActivate: [activeUserGuard, navigationAccessGuard, roleGuard],
    loadChildren: () =>
      import('./features/system-settings/system-settings.module').then(
        (m) => m.SystemSettingsModule,
      ),
    data: {
      breadcrumb: 'breadcrumb.systemSettings',
      requiredRoles: [UserRole.ADMIN, UserRole.OPERATIONAL_ADMIN, UserRole.TECH_ADMIN],
    },
  },

  // Operating routes - Admin and Operator, plus Operational Admin for loading verification only
  //
  // Operational Admin is admitted at this level because Angular runs a parent's canActivate
  // before activating any child, so a role excluded here cannot reach the child route either.
  // The module's own routes then restrict every screen except loading-verification back to
  // Admin and Operator — see operating-routing.module.ts. Widening the module wholesale would
  // hand Operational Admin the loading list and booking overview as well, which the backend
  // read roles would happily serve.
  //
  // Holding observeronly does NOT grant this module: it only turns the modules a user already
  // reaches through their functional role into read-only (action buttons hidden, backend 403s).
  {
    path: 'operating',
    canActivate: [activeUserGuard, navigationAccessGuard, roleGuard],
    loadChildren: () =>
      import('./features/operating/operating.module').then((m) => m.OperatingModule),
    data: {
      breadcrumb: 'breadcrumb.operating',
      requiredRoles: [UserRole.ADMIN, UserRole.OPERATOR, UserRole.OPERATIONAL_ADMIN],
    },
  },

  // Carrier routes - Admin and Carrier only (observeronly grants no access, see above)
  {
    path: 'carrier',
    canActivate: [activeUserGuard, navigationAccessGuard, roleGuard],
    loadChildren: () => import('./features/carrier/carrier.module').then((m) => m.CarrierModule),
    data: {
      breadcrumb: 'breadcrumb.carrier',
      requiredRoles: [UserRole.ADMIN, UserRole.CARRIER],
    },
  },

  // Expedition routes - Admin and Dispatcher only (observeronly grants no access)
  {
    path: 'expedition',
    canActivate: [activeUserGuard, navigationAccessGuard, roleGuard],
    loadChildren: () =>
      import('./features/expedition/expedition.module').then((m) => m.ExpeditionModule),
    data: {
      breadcrumb: 'breadcrumb.expedition',
      requiredRoles: [UserRole.ADMIN, UserRole.DISPATCHER],
    },
  },

  // Report routes - Access controlled at child route level
  // Downloads: accessible via reportAccessGuard (role or CWID in access list)
  // Other reports: Admin and Material Admin only
  {
    path: 'report',
    canActivate: [activeUserGuard, navigationAccessGuard],
    loadChildren: () => import('./features/report/report.module').then((m) => m.ReportModule),
    data: {
      breadcrumb: 'breadcrumb.report',
    },
  },

  // Broadcast Tendering routes - Admin and Broadcast Tendering roles
  {
    path: 'broadcast-tendering',
    canActivate: [activeUserGuard, navigationAccessGuard, roleGuard],
    loadChildren: () =>
      import('./features/broadcast-tendering/broadcast-tendering.module').then(
        (m) => m.BroadcastTenderingModule,
      ),
    data: {
      breadcrumb: 'breadcrumb.broadcastTendering',
      requiredRoles: [
        UserRole.ADMIN,
        UserRole.BROADCAST_TENDERING_ADMIN,
        UserRole.BROADCAST_TENDERING_CARRIER,
      ],
    },
  },

  // Load Tendering routes - Admin and P2P Load Tendering roles
  {
    path: 'load-tendering',
    canActivate: [activeUserGuard, navigationAccessGuard, roleGuard],
    loadChildren: () =>
      import('./features/load-tendering/load-tendering.module').then((m) => m.LoadTenderingModule),
    data: {
      breadcrumb: 'breadcrumb.loadTendering',
      requiredRoles: [
        UserRole.ADMIN,
        UserRole.P2P_LOAD_TENDERING_ADMIN,
        UserRole.P2P_LOAD_TENDERING_CARRIER,
      ],
    },
  },

  // PoD routes - Admin and Process POD only
  {
    path: 'pod',
    canActivate: [activeUserGuard, navigationAccessGuard, roleGuard],
    loadChildren: () => import('./features/pod/pod.module').then((m) => m.PodModule),
    data: {
      breadcrumb: 'breadcrumb.proofOfDelivery',
      requiredRoles: [UserRole.ADMIN, UserRole.PROCESS_POD],
    },
  },

  // Help & Support page - accessible by all authenticated users
  {
    path: 'help-support',
    canActivate: [activeUserGuard],
    loadComponent: () =>
      import('./features/help-support/help-support.component').then((c) => c.HelpSupportComponent),
    data: { breadcrumb: 'breadcrumb.helpSupport' },
  },

  // 404 Not Found page
  {
    path: '404',
    loadComponent: () =>
      import('./components/not-found/not-found.component').then((c) => c.NotFoundComponent),
  },

  { path: '**', redirectTo: '/404' },
];
